<template>
  <div class="customer-addresses-modal">

    <div class="modal-header">
      <h1 class="headline" v-text="$t('customer-addresses-modal-headline')"></h1>
      <h2 class="subheadline" v-text="$t('customer-addresses-modal-subheadline')"></h2>
    </div>

    <div class="oido-modal-content has-padding">

      <div class="validation-errors global" v-if="errors && errors.global">
        <span class="validation-error" v-for="error of errors.global" v-text="error" :key="error"></span>
      </div>

      <OidoAddressInput :label="$t('customer-addresses-modal-billing-address-label')"
        :errors="errors.billingAddress" v-model="billingAddress"></OidoAddressInput>

      
        <ion-checkbox slot="start" v-model="deliveryUsesDifferentAddress"
          name="deliveryUsesDifferentAddress" labelPlacement="end"
          v-text="$t('customer-addresses-modal-use-different-delivery-address-label')"></ion-checkbox>
      

      <OidoAddressInput v-if="deliveryUsesDifferentAddress"
        :label="$t('customer-addresses-modal-delivery-address-label')"
        :hide-type-selector="true"
        :is-delivery-address="true"
        :errors="errors.deliveryAddress" v-model="deliveryAddress"></OidoAddressInput>

      <div class="no-delivery-zone-warning" v-if="noDeliveryZoneForAddresses"
        v-text="$t('customer-addresses-modal-no-delivery-zone-warning')"></div>

    </div>

    <div class="buttons">
      <ion-button class="note-button negative" color="dark" fill="clear" @click="cancel"
        v-text="$t('cancel-button-label')"></ion-button>
      <oido-button class="save-button" @click="save" :show-spinner="processing" :primary="true"
        buttonStyle="solid" :label="$t('customer-addresses-modal-save-button-label')"></oido-button>
    </div>
  </div>
</template>

<script setup>
import OidoButton from '../components/OidoButton.vue';
import OidoAddressInput from '../components/OidoAddressInput.vue';
import {
  IonButton,
  IonCheckbox,
  IonItem,
  modalController
} from "@ionic/vue";

import validateAddress from '../helpers/validateAddress';
import customerService from '../composables/customerService';
import companyService from '../composables/companyService';
</script>
<script>
export default {
  props: [],
  data() {
    return {
      billingAddress: {},
      deliveryAddress: {},
      deliveryUsesDifferentAddress: false,
      errors: {},
      processing: false,
    }
  },
  computed: {
    customer() {
      return customerService.customer ? customerService.customer.value : null
    },
    company() {
      return companyService.company ? companyService.company.value : null
    },
    fallbackCountryCode() {
      let company = this.company
      if (!company) {
        return null
      }
      if (company.taxInfos && company.taxInfos.country) {
        return company.taxInfos.country
      }
      if (company.country) {
        return company.country
      }
      return null
    },
    noDeliveryZoneForAddresses() {
      let relevant = this.deliveryUsesDifferentAddress ? this.deliveryAddress : this.billingAddress
      if (!relevant || !relevant.countryCode || !relevant.zip) {
        return false
      }
      return !this.addressMatchesAnyDeliveryZone(relevant)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.prefill()
    })
  },
  methods: {
    prefill() {
      let customer = this.customer
      if (customer && customer.billingAddress) {
        this.billingAddress = JSON.parse(JSON.stringify(customer.billingAddress))
      } else {
        this.billingAddress = {}
      }
      if (customer && customer.deliveryAddress) {
        this.deliveryAddress = JSON.parse(JSON.stringify(customer.deliveryAddress))
      } else {
        this.deliveryAddress = {}
      }
      this.deliveryUsesDifferentAddress = Boolean(customer && customer.deliveryUsesDifferentAddress)

      let fallback = this.fallbackCountryCode
      if (fallback) {
        if (!this.billingAddress.countryCode) {
          this.billingAddress.countryCode = fallback
        }
        if (!this.deliveryAddress.countryCode) {
          this.deliveryAddress.countryCode = fallback
        }
      }
    },
    addressMatchesAnyDeliveryZone(address) {
      let company = this.company
      let zones = company && company.deliveryZones
      if (!zones || zones.length === 0) {
        return false
      }
      let zip = ('' + address.zip).trim().split(' ').join('')
      for (let zone of zones) {
        if (!zone.countries) continue
        for (let countryRule of zone.countries) {
          if (countryRule.code !== address.countryCode) continue
          let rule = countryRule.validInZipCodesRule || 'all'
          if (rule === 'all') return true
          let configZips = countryRule.validInZipCodesConfig
            ? ('' + countryRule.validInZipCodesConfig).split(',').map(s => s.trim().split(' ').join(''))
            : []
          let matchesPattern = false
          for (let template of configZips) {
            if (template === zip) { matchesPattern = true; break }
            if (template.length !== zip.length) continue
            let ok = true
            for (let i = 0; i < template.length; i++) {
              if (template[i] !== '*' && template[i] !== zip[i]) { ok = false; break }
            }
            if (ok) { matchesPattern = true; break }
          }
          if (rule === 'only' && matchesPattern) return true
          if (rule === 'except' && !matchesPattern) return true
        }
      }
      return false
    },
    cancel() {
      modalController.dismiss(false);
    },
    async save() {
      this.errors = {}
      this.processing = true

      let billingValidation = validateAddress(this.billingAddress, this.$t)
      if (!billingValidation.passed) {
        this.errors.billingAddress = billingValidation.errors
      }

      if (this.deliveryUsesDifferentAddress) {
        let deliveryValidation = validateAddress(this.deliveryAddress, this.$t)
        if (!deliveryValidation.passed) {
          this.errors.deliveryAddress = deliveryValidation.errors
        }
      }

      if (Object.keys(this.errors).length > 0) {
        this.processing = false
        return
      }

      let companyId = this.customer && this.customer.companyId
      let result = await customerService.updateAddresses(companyId, {
        billingAddress: this.billingAddress,
        deliveryAddress: this.deliveryUsesDifferentAddress ? this.deliveryAddress : null,
        deliveryUsesDifferentAddress: this.deliveryUsesDifferentAddress,
      })

      this.processing = false

      if (!result || (result.errors && Object.keys(result.errors).length > 0)) {
        if (result && result.errors) {
          let translatedErrors = {}
          for (let key in result.errors) {
            let value = result.errors[key]
            if (Array.isArray(value)) {
              translatedErrors[key] = value.map((errorName) => this.$t(errorName))
            } else if (value && typeof value == 'object') {
              translatedErrors[key] = {}
              for (let subKey in value) {
                translatedErrors[key][subKey] = (value[subKey] || []).map((errorName) => this.$t(errorName))
              }
            }
          }
          this.errors = translatedErrors
        }
        return
      }

      modalController.dismiss(true)
    },
  },
}
</script>
<style scoped>
.customer-addresses-modal {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: white;
}

.customer-addresses-modal .oido-modal-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  overflow: hidden;
  overflow-y: auto;
  padding: 0.2rem;
  padding: 1rem 2rem;
}

.customer-addresses-modal .oido-modal-content .oido-address-input {
  padding: 0;
  margin: 0;
  margin-bottom: 1rem;
}

.customer-addresses-modal .modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem 2rem 0rem;
  text-align: center;
}

.customer-addresses-modal .modal-header .headline {
  margin-top: 0;
  font-size: 1.2rem;
  margin-bottom: 0.3rem;
}

.customer-addresses-modal .modal-header .subheadline {
  margin-top: 0;
  font-size: 1rem;
  color: #666;
}

.delivery-toggle {
  margin: 0.5rem 0 1rem;
}

ion-checkbox::part(label) {
  text-wrap: wrap;
}

.customer-addresses-modal .buttons {
  display: flex;
  align-items: center;
  justify-content: end;
  width: 100%;
  padding: 1rem 2rem 1.5rem;
}

.customer-addresses-modal .buttons ion-button {
  margin: 0 0.5rem;
}

.validation-errors.global {
  margin-bottom: 1rem;
  text-align: left;
}

.no-delivery-zone-warning {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.4rem;
  background-color: #fff4e5;
  color: #8a4b00;
  border: 1px solid #ffd9a8;
  font-size: 0.9rem;
  text-align: left;
}
</style>
<style>
.customer-addresses-modal .oido-address-input > ion-label:first-child{
  text-align: center;
}
.customer-addresses-modal .oido-button{
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}
</style>